import type { CSSProperties } from 'react'
import { Progress } from '@/components/ui/progress'
import { gib, poolSizeText, poolText } from '@/ui/format'
import type { Amount } from '@/views/model'

// 자원 막대 하나. 막대 전체가 전체 양이고 연한 층이 할당, 그 위의 진한 층이 사용량이다.
// 글자는 이름과 `할당 / 전체` 뿐이고 사용량의 수는 접근성 이름과 툴팁에 있다. 전체를 모르면 막대는 비고 `4 / —코어` — 할당은 우리 원장이라 늘 있다.
// 크기(`size`, 값은 MB)는 단위를 받지 않는다 — 전체가 GB·TB 를 정하고 할당·사용량도 그 단위다(`ui/format.ts`).
// 색은 차트 토큰이고 임계값으로 바꾸지 않는다 — 들어가는지 마는지는 시스템이 정하지 이 막대가 말하지 않는다.
// 두 꼴이 있다: 클러스터 카드의 것(이름·값이 위, 6px 막대가 아래)과 노드 카드의 한 줄(`row` — 이름 · 4px 막대 · 값이 한 줄).

const pct = (n: number, total: number) => Math.min(100, Math.max(0, (n / total) * 100))

function Layers({ allocPct, usedPct, thin }: { allocPct: number; usedPct: number; thin: boolean }) {
  const h = thin ? 'h-1' : 'h-1.5'
  return (
    <div className={`relative ${h}`}>
      <Progress aria-hidden value={allocPct} className={`${h} bg-border [&>div]:bg-(--bar) [&>div]:opacity-35`} />
      <Progress aria-hidden value={usedPct} className={`absolute inset-0 ${h} bg-transparent [&>div]:bg-(--bar)`} />
    </div>
  )
}

export function PoolBar({ label, amount, unit, color, row = false }: { label: string; amount: Amount; unit: '코어' | '장' | 'size'; color: string; row?: boolean }) {
  const total = amount.total !== null && amount.total > 0 ? amount.total : null
  const allocPct = total === null ? 0 : pct(amount.allocated, total)
  const usedPct = total === null || amount.used === null ? 0 : pct(amount.used, total)
  const text =
    unit === 'size'
      ? poolSizeText(amount.allocated / 1024, gib(amount.total), gib(amount.used))
      : poolText(amount.allocated, amount.total, amount.used, unit)
  const alloc = `${text.amount}${text.unit}`
  const name = `${label} 할당 ${alloc} · ${text.used}`
  const style = { '--bar': color } as CSSProperties
  if (row) {
    // 이름과 값의 폭이 고정이라 줄이 넷 쌓여도 막대의 시작과 끝, 값의 시작점이 맞는다(값은 왼쪽 정렬). 값 칸은 64px(수 40px · 간격 4px · 단위, 화면으로 본 치수) — 수의 칸(최소 40px)과 단위의 칸이 따로라 단위의 시작점도 맞고, 단위는 수보다 작은 글자다. 막대가 남는 폭을 다 가지므로 값 칸을 넓히면 막대가 짧아진다.
    return (
      <div role="img" aria-label={name} title={name} className="flex items-center gap-2" style={style}>
        <span className="w-11 flex-none text-[11px] font-medium text-muted-foreground">{label}</span>
        <div className="min-w-0 flex-1">
          <Layers allocPct={allocPct} usedPct={usedPct} thin />
        </div>
        <span className="flex w-16 flex-none items-baseline gap-1 text-[11px] tabular-nums text-muted-foreground">
          <span className="min-w-10 whitespace-nowrap">{text.amount}</span>
          <span className="text-[9px]">{text.unit}</span>
        </span>
      </div>
    )
  }
  return (
    <div role="img" aria-label={name} title={name} className="min-w-0" style={style}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium">{label}</span>
        <span className="truncate text-[12px] tabular-nums text-muted-foreground">{alloc}</span>
      </div>
      <div className="mt-1.5">
        <Layers allocPct={allocPct} usedPct={usedPct} thin={false} />
      </div>
    </div>
  )
}

/** 클러스터의 막대 넷 — CPU · 메모리 · 스토리지 · GPU 가 2×2 로 놓인다(칸마다 위에 이름과 값, 아래에 막대). 노드 카드의 줄과 같은 순서·같은 색이다. */
export function PoolBars({ cpu, memory, storage, gpu }: { cpu: Amount; memory: Amount; storage: Amount; gpu: Amount }) {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-3 max-sm:gap-x-4">
      <PoolBar label="CPU" amount={cpu} unit="코어" color="var(--chart-1)" />
      <PoolBar label="메모리" amount={memory} unit="size" color="var(--chart-2)" />
      <PoolBar label="스토리지" amount={storage} unit="size" color="var(--chart-3)" />
      <PoolBar label="GPU" amount={gpu} unit="장" color="var(--chart-4)" />
    </div>
  )
}

/** 노드 카드의 줄 넷 — CPU · 메모리 · 스토리지 · GPU 가 한 줄에 하나씩 쌓인다. 클러스터 카드의 막대와 같은 순서·같은 색이다. */
export function PoolBarRows({ cpu, memory, storage, gpu }: { cpu: Amount; memory: Amount; storage: Amount; gpu: Amount }) {
  return (
    <div className="flex flex-col">
      <PoolBar row label="CPU" amount={cpu} unit="코어" color="var(--chart-1)" />
      <PoolBar row label="메모리" amount={memory} unit="size" color="var(--chart-2)" />
      <PoolBar row label="스토리지" amount={storage} unit="size" color="var(--chart-3)" />
      <PoolBar row label="GPU" amount={gpu} unit="장" color="var(--chart-4)" />
    </div>
  )
}
